import React from 'react';
import styled from 'styled-components';
import dayjs from 'dayjs';
import { Button, Col, Empty, Input, Modal, Progress, Row, Segmented, Select, Spin } from 'antd';
import {
  CameraOutlined,
  CheckCircleFilled,
  PlayCircleOutlined,
  ReloadOutlined,
  TeamOutlined
} from '@ant-design/icons';
import { countItems, pressable } from 'src/constants/checklists';
import { assigneeNames, everyoneAssigned } from 'src/constants/checklistAccess';
import { STATUS_FILTERS, filterRuns, peopleInRuns } from 'src/constants/checklistTeam';
import {
  allItems,
  runProgress,
  runState,
  runSubtitle,
  sortRuns
} from 'src/constants/checklistRuns';

const GREEN = '#2f8a4c';
const RED = '#c8442b';
const AMBER = '#b9770e';
const MUTED = '#5b6660';

const TONE = {
  success: { color: GREEN, bg: '#eaf6ee' },
  warning: { color: AMBER, bg: '#fdf3e2' },
  error: { color: RED, bg: '#fdecea' },
  default: { color: MUTED, bg: '#f1f4f2' }
};

const Summary = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5em;
  margin-bottom: 1.1em;

  .date {
    font-weight: 700;
    margin-right: 0.4em;
  }
  .chip {
    border-radius: 999px;
    padding: 0.25em 0.8em;
    font-size: 13px;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
  }
  .spacer {
    flex: 1;
  }
`;

const Heading = styled.h3`
  font-family: Nunito, sans-serif;
  font-weight: 800;
  font-size: 13px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${MUTED};
  margin: 1.6em 0 0.6em;
`;

// The whole card is the tap target — staff are often one-handed.
const RunCard = styled.div`
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: 0.9em;
  width: 100%;
  min-height: 5.2em;
  padding: 0.9em 1em;
  background: #fff;
  border-radius: 14px;
  border: 1px solid #e3ebe5;
  box-shadow: inset 4px 0 0 ${p => p.stripe};
  cursor: pointer;
  transition: box-shadow 0.15s, transform 0.12s;

  &:hover {
    box-shadow: inset 4px 0 0 ${p => p.stripe}, 0 6px 16px rgba(20, 40, 28, 0.08);
  }
  &:active {
    transform: scale(0.99);
  }
  &:focus-visible {
    outline: 2px solid ${GREEN};
    outline-offset: 2px;
  }

  .text {
    flex: 1;
    min-width: 0;
  }
  .name {
    font-weight: 700;
    font-size: 15.5px;
    line-height: 1.3;
  }
  .sub {
    color: ${MUTED};
    font-size: 13px;
    margin-top: 0.1em;
  }
  .who {
    color: ${MUTED};
    font-size: 12.5px;
    margin-top: 0.15em;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .state {
    display: inline-block;
    margin-top: 0.35em;
    font-size: 12.5px;
    font-weight: 700;
    border-radius: 999px;
    padding: 0.1em 0.65em;
  }
  .finished {
    margin-left: 0.5em;
    color: ${MUTED};
    font-size: 12.5px;
    font-variant-numeric: tabular-nums;
  }
  .ant-progress-text {
    font-size: 12px !important;
    font-weight: 700;
  }
`;

const StartCard = styled.div`
  display: flex;
  align-items: center;
  gap: 0.9em;
  padding: 0.85em 1em;
  background: #fff;
  border-radius: 14px;
  border: 1px dashed #c9d6cd;

  .text {
    flex: 1;
    min-width: 0;
  }
  .name {
    font-weight: 700;
  }
  .sub {
    color: ${MUTED};
    font-size: 13px;
  }
`;

const Filters = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.6em;
  margin-bottom: 1em;
  .ant-select {
    min-width: 12em;
  }
  @media (max-width: 575px) {
    .ant-select,
    .ant-segmented {
      flex: 1 1 100%;
    }
  }
`;

const TodayView = ({
  runs: allRuns = [],
  templates = [],
  loading,
  now,
  onOpen,
  onStart,
  onRefresh,
  canFilter = false,
  filters = {},
  onFiltersChange
}) => {
  const [startFor, setStartFor] = React.useState(null);
  const [fields, setFields] = React.useState({});
  const [starting, setStarting] = React.useState(false);

  const current = now();
  const people = canFilter ? peopleInRuns(allRuns) : [];
  const departments = Array.from(new Set(allRuns.map(r => r.department).filter(Boolean))).sort();
  const filtering = Boolean(filters.person || filters.department || (filters.status && filters.status !== 'all'));
  const runs = canFilter ? filterRuns(allRuns, filters, current) : allRuns;
  const setFilter = patch => onFiltersChange && onFiltersChange({ ...filters, ...patch });
  const sorted = sortRuns(runs, current);
  const states = sorted.map(run => ({ run, state: runState(run, current) }));

  const counts = states.reduce(
    (acc, { state }) => {
      if (state.key === 'done' || state.key === 'late') acc.done += 1;
      else if (state.key === 'overdue') acc.overdue += 1;
      else acc.todo += 1;
      return acc;
    },
    { todo: 0, overdue: 0, done: 0 }
  );

  const open = states.filter(s => s.state.key !== 'done' && s.state.key !== 'late');
  const finished = states.filter(s => s.state.key === 'done' || s.state.key === 'late');
  const onDemand = templates.filter(
    t => t.active !== false && t.schedule && t.schedule.frequency === 'onDemand'
  );

  const beginStart = template => {
    if (!(template.runFields || []).length) {
      setStarting(true);
      // The page already shows the error message.
      onStart(template, {})
        .catch(() => {})
        .finally(() => setStarting(false));
      return;
    }
    setFields({});
    setStartFor(template);
  };

  const confirmStart = () => {
    const missing = (startFor.runFields || []).filter(
      f => f.required !== false && !String(fields[f.key] || '').trim()
    );
    if (missing.length) return;
    setStarting(true);
    onStart(startFor, fields)
      .then(() => setStartFor(null))
      .catch(() => {})
      .finally(() => setStarting(false));
  };

  const card = ({ run, state }) => {
    const progress = runProgress(run);
    const tone = TONE[state.tone] || TONE.default;
    const photos = allItems(run).filter(i => i.photoRequired).length;
    const done = state.key === 'done' || state.key === 'late';
    const who = assigneeNames(everyoneAssigned(run));
    return (
      <Col xs={24} md={12} xl={8} key={run._id}>
        <RunCard stripe={tone.color} {...pressable(() => onOpen(run))}>
          {done ? (
            <CheckCircleFilled style={{ fontSize: 40, color: tone.color }} />
          ) : (
            <Progress
              type="circle"
              percent={progress.percent}
              size={46}
              strokeColor={tone.color}
              format={() => `${progress.answered}/${progress.total}`}
            />
          )}
          <span className="text">
            <div className="name">{run.templateName}</div>
            <div className="sub">
              {runSubtitle(run) || run.department}
              {photos > 0 && !done && (
                <>
                  {' · '}
                  <CameraOutlined /> {photos}
                </>
              )}
            </div>
            {who && (
              <div className="who">
                <TeamOutlined /> {who}
              </div>
            )}
            <span className="state" style={{ color: tone.color, background: tone.bg }}>
              {state.label}
            </span>
            {done && run.completedAt && (
              <span className="finished">
                {dayjs(run.completedAt).format('HH:mm')}
                {run.completedByName ? ` · ${run.completedByName}` : ''}
              </span>
            )}
          </span>
        </RunCard>
      </Col>
    );
  };

  return (
    <Spin spinning={Boolean(loading)}>
      <Summary>
        <span className="date">{dayjs(current).format('dddd D MMMM')}</span>
        {counts.overdue > 0 && (
          <span className="chip" style={{ color: RED, background: TONE.error.bg }}>
            {counts.overdue} overdue
          </span>
        )}
        <span className="chip" style={{ color: MUTED, background: TONE.default.bg }}>
          {counts.todo} to do
        </span>
        <span className="chip" style={{ color: GREEN, background: TONE.success.bg }}>
          {counts.done} done
        </span>
        <span className="spacer" />
        <Button icon={<ReloadOutlined />} onClick={onRefresh}>
          Refresh
        </Button>
      </Summary>

      {canFilter && allRuns.length > 0 && (
        <Filters>
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="Everyone"
            value={filters.person || undefined}
            onChange={value => setFilter({ person: value || null })}
            options={people.map(p => ({ value: p.id, label: p.name }))}
          />
          {departments.length > 1 && (
            <Select
              allowClear
              placeholder="All departments"
              value={filters.department || undefined}
              onChange={value => setFilter({ department: value || null })}
              options={departments.map(d => ({ value: d, label: d }))}
            />
          )}
          <Segmented
            value={filters.status || 'all'}
            onChange={value => setFilter({ status: value })}
            options={STATUS_FILTERS}
          />
          {filtering && (
            <Button type="link" onClick={() => onFiltersChange && onFiltersChange({})}>
              Clear
            </Button>
          )}
        </Filters>
      )}

      {filtering && !runs.length ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="Nothing matches these filters."
          style={{ padding: '3em 0' }}
        />
      ) : !runs.length && !onDemand.length ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="Nothing scheduled for today."
          style={{ padding: '3em 0' }}
        />
      ) : (
        <>
          {open.length > 0 && <Row gutter={[12, 12]}>{open.map(card)}</Row>}
          {!open.length && runs.length > 0 && !filtering && (
            <div style={{ color: GREEN, fontWeight: 600, padding: '0.5em 0' }}>
              <CheckCircleFilled /> Everything for today is done.
            </div>
          )}

          {onDemand.length > 0 && (
            <>
              <Heading>Start a checklist</Heading>
              <Row gutter={[12, 12]}>
                {onDemand.map(t => (
                  <Col xs={24} md={12} xl={8} key={t._id}>
                    <StartCard>
                      <span className="text">
                        <div className="name">{t.name}</div>
                        <div className="sub">
                          {t.description || `${countItems(t)} items`}
                        </div>
                        {everyoneAssigned(t).length > 0 && (
                          <div className="sub">
                            <TeamOutlined /> {assigneeNames(everyoneAssigned(t))}
                          </div>
                        )}
                      </span>
                      <Button
                        type="primary"
                        icon={<PlayCircleOutlined />}
                        loading={starting && !startFor}
                        onClick={() => beginStart(t)}
                        style={{ backgroundColor: '#1c1c1c' }}
                      >
                        Start
                      </Button>
                    </StartCard>
                  </Col>
                ))}
              </Row>
            </>
          )}

          {finished.length > 0 && (
            <>
              <Heading>Completed</Heading>
              <Row gutter={[12, 12]}>{finished.map(card)}</Row>
            </>
          )}
        </>
      )}

      <Modal
        open={Boolean(startFor)}
        title={startFor ? startFor.name : ''}
        okText="Start"
        confirmLoading={starting}
        okButtonProps={{
          style: { backgroundColor: '#1c1c1c' },
          disabled:
            !startFor ||
            (startFor.runFields || []).some(
              f => f.required !== false && !String(fields[f.key] || '').trim()
            )
        }}
        onOk={confirmStart}
        onCancel={() => setStartFor(null)}
      >
        {((startFor && startFor.runFields) || []).map((field, index) => (
          <div key={field.key || index} style={{ marginBottom: '0.8em' }}>
            <div style={{ fontWeight: 600, marginBottom: '0.3em' }}>{field.label}</div>
            <Input
              size="large"
              autoFocus={index === 0}
              value={fields[field.key] || ''}
              onChange={e => {
                const value = e.target.value;
                setFields(f => ({ ...f, [field.key]: value }));
              }}
              onPressEnter={confirmStart}
            />
          </div>
        ))}
      </Modal>
    </Spin>
  );
};

export default TodayView;
